import type { Locale } from '@seek/melways-sites';
import { useTranslations } from '@vocab/react';
import { Text, Box, Stack } from 'braid-design-system';
import { observer } from 'mobx-react';
import React, { type ReactNode, useState } from 'react';

import { ACTION_ERROR } from '../../../stores/state';

import translations from './.vocab';
import ItemContent from './ItemContent';
import ItemMeta from './ItemMeta';

export type ItemType = 'note' | 'attachment';

interface Props {
  children: ReactNode;
  type: ItemType;
  user: string;
  date: string;
  isNew: boolean;
  onHide: () => void;
  onDelete: () => Promise<string>;
  canDelete: boolean;
  locale: Locale;
}

const Item = ({
  type,
  user,
  date,
  isNew,
  onHide,
  onDelete,
  canDelete,
  children,
  locale,
}: Props) => {
  const { t } = useTranslations(translations);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const [showServerError, setShowServerError] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleDelete = async () => {
    setLoading(true);
    setIsConfirmingDelete(false);

    const state = await onDelete();
    if (state === ACTION_ERROR) {
      setShowServerError(true);
      setLoading(false);
    }
  };

  const onClickDelete = () => {
    setShowServerError(false);
    setIsConfirmingDelete(true);
  };

  return (
    <Stack space="gutter">
      <ItemMeta
        user={user}
        date={date}
        isNew={isNew}
        onHide={onHide}
        locale={locale}
      />

      <Box display="flex" alignItems="flexEnd">
        <ItemContent
          type={type}
          canDelete={canDelete}
          onClickDelete={onClickDelete}
          loading={loading}
          isConfirmingDelete={isConfirmingDelete}
          onDeleteCancelled={() => setIsConfirmingDelete(false)}
          onDeleteConfirmed={handleDelete}
        >
          {children}
        </ItemContent>
      </Box>
      {showServerError && (
        <Box marginBottom="small">
          <Text tone="critical">
            {t('Sorry, we had an issue deleting....')}
          </Text>
        </Box>
      )}
    </Stack>
  );
};

export default observer(Item);
